import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { CampaignVisibilityFilter } from "@intentsify/dto";
import { Option } from "@intentsify/types";
import { CampaignsTableFiltersState, emptyCampaignsTableFilters } from "api";
import {
  Button,
  DayPicker,
  FILTER_LABEL_TEXT_PROPS,
  Filter,
  Popover,
} from "components";
import { useCompanies } from "components/CompanySelector/CompanySelector.state";
import { useEffect, useMemo, useState } from "react";
import { useComponentColors } from "theme";
import { useCampaignCreators } from "../YourCampaigns.requests";

const VISIBILITY_OPTIONS: Option<CampaignVisibilityFilter>[] = [
  { value: "visible", label: "Visible" },
  { value: "hidden", label: "Hidden" },
];

type CampaignsTableFiltersProps = {
  filters: CampaignsTableFiltersState;
  onChange: (filters: CampaignsTableFiltersState) => void;
  showAdminFilters: boolean;
  showDraftsToggle: boolean;
};

type AdminCampaignsFiltersProps = Pick<
  CampaignsTableFiltersProps,
  "filters" | "onChange"
>;

// Admin-only dropdowns live in a child component so their data-fetching
// hooks (creators, companies) only run when an admin actually renders them.
const AdminCampaignsFilters = ({
  filters,
  onChange,
}: AdminCampaignsFiltersProps) => {
  const { data: creatorsData, isLoading: isLoadingCreators } =
    useCampaignCreators();
  const { companies, isLoading: isLoadingCompanies } = useCompanies();

  const creatorOptions = useMemo(
    () =>
      (creatorsData?.creators ?? []).map((creator) => ({
        value: creator.id,
        label: creator.displayName || `User ${creator.id}`,
      })),
    [creatorsData],
  );

  const companyOptions = useMemo(
    () =>
      (companies ?? []).map((company) => ({
        value: company.companyId,
        label: company.displayName,
      })),
    [companies],
  );

  return (
    <>
      <Filter<number, unknown>
        label="Created By"
        placeholder="All creators"
        isLoading={isLoadingCreators}
        isClearable
        minW="200px"
        options={creatorOptions}
        // Build the selection from the filter ids directly (with a synthetic
        // fallback option) rather than by filtering the loaded options, so an
        // id restored from the URL survives even when it isn't in the cached
        // creators list yet — otherwise Filter's mount echo would wipe it.
        selected={filters.createdByIds.map(
          (id) =>
            creatorOptions.find((option) => option.value === id) ?? {
              value: id,
              label: `User ${id}`,
            },
        )}
        onFilterValuesChange={(options) => {
          onChange({
            ...filters,
            createdByIds: options.map((option) => option.value),
          });
        }}
      />
      <Filter<number, unknown>
        label="Company"
        placeholder="All companies"
        isLoading={isLoadingCompanies}
        isClearable
        minW="220px"
        options={companyOptions}
        selected={filters.companyIds.map(
          (id) =>
            companyOptions.find((option) => option.value === id) ?? {
              value: id,
              label: `Company ${id}`,
            },
        )}
        onFilterValuesChange={(options) => {
          onChange({
            ...filters,
            companyIds: options.map((option) => option.value),
          });
        }}
      />
    </>
  );
};

// Occasional filters (visibility + creation date range) live behind a
// "More filters" popover so the toolbar stays compact; the trigger carries a
// count badge so applied-but-collapsed filters remain discoverable.
const MoreFiltersPopover = ({
  filters,
  onChange,
}: AdminCampaignsFiltersProps) => {
  const componentColors = useComponentColors();
  const [isOpen, setIsOpen] = useState(false);

  const activeCount =
    (filters.visibility ? 1 : 0) +
    (filters.createdFrom ? 1 : 0) +
    (filters.createdTo ? 1 : 0);

  const clearAdvancedFilters = () =>
    onChange({
      ...filters,
      visibility: undefined,
      createdFrom: undefined,
      createdTo: undefined,
    });

  // Close on an outside click, but keep the popover open while interacting with
  // its own trigger/body or the Visibility menu (which portals to document.body
  // via a `.chakra-portal`). The DayPicker calendar renders inside the body, so
  // it's covered by the body check.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (
        target.closest("[data-more-filters-trigger]") ||
        target.closest("[data-more-filters-body]") ||
        target.closest(".chakra-portal")
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  return (
    <Popover
      width="280px"
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      // Outside-click closing is handled manually so interacting with the
      // portaled Visibility menu doesn't dismiss the popover mid-edit.
      closeOnBlur={false}
      popoverTrigger={
        <Box data-more-filters-trigger display="inline-flex">
          <Button variant="ghost" size="sm" rightIcon={<ChevronDownIcon />}>
            More filters
          </Button>
        </Box>
      }
      popoverBody={
        <Stack data-more-filters-body p={4} spacing={4}>
          <Filter<CampaignVisibilityFilter, unknown>
            label="Visibility"
            placeholder="All"
            isLoading={false}
            isClearable
            isMulti={false}
            options={VISIBILITY_OPTIONS}
            selected={VISIBILITY_OPTIONS.filter(
              (option) => option.value === filters.visibility,
            )}
            onFilterValuesChange={(options) =>
              onChange({
                ...filters,
                visibility: options[0]?.value,
              })
            }
          />
          <Stack>
            <Text
              {...FILTER_LABEL_TEXT_PROPS}
              color={componentColors.form.formLabelColor}
            >
              Created From:
            </Text>
            <DayPicker
              key={`created-from-${filters.createdFrom ?? ""}`}
              initialValue={filters.createdFrom}
              onChange={(day) => onChange({ ...filters, createdFrom: day })}
            />
          </Stack>
          <Stack>
            <Text
              {...FILTER_LABEL_TEXT_PROPS}
              color={componentColors.form.formLabelColor}
            >
              Created To:
            </Text>
            <DayPicker
              key={`created-to-${filters.createdTo ?? ""}`}
              initialValue={filters.createdTo}
              onChange={(day) => onChange({ ...filters, createdTo: day })}
            />
          </Stack>
        </Stack>
      }
      popoverFooter={
        activeCount > 0 ? (
          <Button variant="link-teal" size="sm" onClick={clearAdvancedFilters}>
            Clear
          </Button>
        ) : null
      }
    />
  );
};

// Surfaces applied "More filters" selections as removable chips so filters
// tucked inside the popover stay visible and one click away from removal.
const ActiveFilterChips = ({
  filters,
  onChange,
}: AdminCampaignsFiltersProps) => {
  const chips: { key: string; label: string; onRemove: () => void }[] = [];

  if (filters.visibility) {
    const visibilityLabel =
      VISIBILITY_OPTIONS.find((option) => option.value === filters.visibility)
        ?.label ?? filters.visibility;
    chips.push({
      key: "visibility",
      label: `Visibility: ${visibilityLabel}`,
      onRemove: () => onChange({ ...filters, visibility: undefined }),
    });
  }

  if (filters.createdFrom) {
    chips.push({
      key: "createdFrom",
      label: `From: ${filters.createdFrom}`,
      onRemove: () => onChange({ ...filters, createdFrom: undefined }),
    });
  }

  if (filters.createdTo) {
    chips.push({
      key: "createdTo",
      label: `To: ${filters.createdTo}`,
      onRemove: () => onChange({ ...filters, createdTo: undefined }),
    });
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <Flex gap={2} flexWrap="wrap">
      {chips.map((chip) => (
        <Tag
          key={chip.key}
          size="md"
          colorScheme="teal"
          borderRadius="full"
          variant="subtle"
        >
          <TagLabel>{chip.label}</TagLabel>
          <TagCloseButton onClick={chip.onRemove} />
        </Tag>
      ))}
    </Flex>
  );
};

const CampaignsTableFilters = ({
  filters,
  onChange,
  showAdminFilters,
  showDraftsToggle,
}: CampaignsTableFiltersProps) => {
  const hasActiveFilters =
    filters.createdByIds.length > 0 ||
    filters.companyIds.length > 0 ||
    Boolean(filters.visibility) ||
    Boolean(filters.createdFrom) ||
    Boolean(filters.createdTo) ||
    filters.isFavorite ||
    filters.onlyDrafts;

  return (
    <Stack spacing={3} align="flex-start" w="100%">
      <Flex alignItems="flex-end" gap={3} flexWrap="wrap">
        {showAdminFilters && (
          <AdminCampaignsFilters filters={filters} onChange={onChange} />
        )}
        <Button
          variant="chip"
          isActive={filters.isFavorite}
          size="sm"
          onClick={() =>
            onChange({ ...filters, isFavorite: !filters.isFavorite })
          }
        >
          Favorites
        </Button>
        {showDraftsToggle && (
          <Button
            variant="chip"
            isActive={filters.onlyDrafts}
            size="sm"
            onClick={() =>
              onChange({ ...filters, onlyDrafts: !filters.onlyDrafts })
            }
          >
            Drafts
          </Button>
        )}
        {showAdminFilters && (
          <MoreFiltersPopover filters={filters} onChange={onChange} />
        )}
        {showAdminFilters && hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ ...emptyCampaignsTableFilters })}
          >
            Clear all
          </Button>
        )}
      </Flex>
      {showAdminFilters && (
        <ActiveFilterChips filters={filters} onChange={onChange} />
      )}
    </Stack>
  );
};

export { CampaignsTableFilters };
